import React, { useEffect, useState, Component } from 'react';
import { Mail, Lock, Eye, EyeOff, AlertTriangle, Loader2 } from 'lucide-react';
import { userProfiles, UserProfile } from '../services/userProfiles';
import { loginWithCognito } from '../services/mcpAuth';
import { WorkiumLogo } from '../components/brand/WorkiumLogo';
// ─── JWT Decode Helper ──────────────────────────────────────────────────────────
function parseJwt(token: string): Record<string, any> | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}
interface LoginPageProps {
  onLogin: (profile: UserProfile) => void;
}
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  // Auto-focus email on mount
  useEffect(() => {
    const el = document.getElementById('email-input');
    if (el) el.focus();
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Por favor ingresá email y contraseña');
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      // 1. Authenticate against Cognito (real auth)
      const authResult = await loginWithCognito(email, password);
      // 2. Check if user is SUPERADMIN — redirect to /superadmin
      const claims = parseJwt(authResult.idToken);
      const role = (claims?.['custom:ROLE'] || claims?.['custom:role'] || '').toUpperCase();
      if (role === 'SUPERADMIN') {
        // Store token for SuperadminPage (uses sessionStorage)
        sessionStorage.setItem('sa_id_token', authResult.idToken);
        sessionStorage.setItem('sa_token_expiry', (Date.now() + authResult.expiresIn * 1000 - 60000).toString());
        sessionStorage.setItem('sa_email', claims?.email || email);
        // Redirect to superadmin panel
        window.location.href = '/superadmin';
        return;
      }
      // 3. Normal user — load existing profile or create a default one
      let profile = userProfiles.authenticateWithoutPassword(email);
      if (!profile) {
        // User exists in Cognito but not in local profiles — create one
        const name = email.split('@')[0].replace(/[._]/g, ' ');
        const initials = name.split(' ').map(w => w[0]?.toUpperCase() || '').join('').slice(0, 2);
        profile = userProfiles.createProfile({
          id: `cognito-${Date.now()}`,
          name: name.replace(/\b\w/g, c => c.toUpperCase()),
          email: email.toLowerCase(),
          role: 'User',
          initials,
          jira: {
            baseUrl: 'https://agentvsm.atlassian.net',
            email,
            apiKey: '',
            projectKey: ''
          },
          confluence: {
            baseUrl: 'https://agentvsm.atlassian.net/wiki',
            email,
            apiKey: '',
            spaceKey: ''
          },
          mcpCredentials: {
            jira: {
              username: email,
              password
            },
            confluence: {
              username: email,
              password
            }
          },
          github: {
            org: 'workiumai'
          }
        }, password);
      }
      onLogin(profile);
    } catch (err: any) {
      const msg = err?.message || 'Error de autenticación';
      if (msg.includes('Incorrect username or password')) {
        setError('Email o contraseña incorrectos');
      } else if (msg.includes('User does not exist')) {
        setError('Usuario no encontrado');
      } else {
        setError(msg);
      }
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen w-full bg-slate-950 flex items-center justify-center relative overflow-hidden font-sans selection:bg-indigo-500/30" data-id="element-6874">
      {/* Subtle background effects */}
      <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-indigo-900/20 blur-[120px] rounded-full pointer-events-none" data-id="element-6875" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-blue-900/10 blur-[120px] rounded-full pointer-events-none" data-id="element-6876" />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wMykiLz48L3N2Zz4=')] opacity-50 pointer-events-none" data-id="element-6877" />

      <div className="w-full max-w-[380px] px-6 relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-700" data-id="element-6878">
        {/* Logo Area */}
        <div className="flex flex-col items-center mb-10" data-id="element-6879">
          <WorkiumLogo size="lg" className="mb-2 drop-shadow-2xl" data-id="element-6880" />
          <p className="text-sm text-indigo-200/60 mt-2 font-medium tracking-wide uppercase" data-id="element-6881">
            Intelligent Work Orchestration
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl p-6 shadow-2xl" data-id="element-6882">
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-6883">
            {/* Email Field */}
            <div data-id="element-6884">
              <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1" data-id="element-6885">
                Email
              </label>
              <div className="relative group" data-id="element-6886">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" data-id="element-6887" />

                <input id="email-input" type="email" value={email} onChange={e => {
                setEmail(e.target.value);
                setError('');
              }} placeholder="tu@empresa.com" className="w-full bg-slate-950/50 border border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all" disabled={isLoading} data-id="element-6888" />
              </div>
            </div>

            {/* Password Field */}
            <div data-id="element-6889">
              <label className="block text-xs font-medium text-slate-400 mb-1.5 ml-1" data-id="element-6890">
                Contraseña
              </label>
              <div className="relative group" data-id="element-6891">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" data-id="element-6892" />

                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => {
                setPassword(e.target.value);
                setError('');
              }} placeholder="••••••••" className="w-full bg-slate-950/50 border border-slate-800 rounded-xl pl-10 pr-10 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all" disabled={isLoading} data-id="element-6893" />

                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors" tabIndex={-1} data-id="element-6894">
                  {showPassword ? <EyeOff size={15} data-id="element-6895" /> : <Eye size={15} data-id="element-6896" />}
                </button>
              </div>
            </div>

            {/* Options Row */}
            <div className="flex items-center justify-between pt-1" data-id="element-6897">
              <label className="flex items-center gap-2 cursor-pointer group" data-id="element-6898">
                <div className="relative flex items-center justify-center w-4 h-4" data-id="element-6899">
                  <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="peer appearance-none w-4 h-4 border border-slate-700 rounded bg-slate-950/50 checked:bg-indigo-600 checked:border-indigo-600 transition-colors cursor-pointer" data-id="element-6900" />

                  <svg className="absolute w-2.5 h-2.5 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" viewBox="0 0 14 14" fill="none" data-id="element-6901">
                    <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" data-id="element-6902" />
                  </svg>
                </div>
                <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors select-none" data-id="element-6903">
                  Recordarme
                </span>
              </label>

              <a href="#" className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors" data-id="element-6904">
                ¿Olvidaste tu contraseña?
              </a>
            </div>

            {/* Error Message */}
            {error && <div className="flex items-center gap-2 px-3 py-2.5 bg-red-950/30 border border-red-900/50 rounded-lg text-xs text-red-400 animate-in fade-in slide-in-from-top-1" data-id="element-6905">
                <AlertTriangle size={14} className="shrink-0" data-id="element-6906" />
                <p data-id="element-6907">{error}</p>
              </div>}

            {/* Submit Button */}
            <button type="submit" disabled={isLoading} className="w-full bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white text-sm font-semibold py-2.5 rounded-xl transition-all shadow-lg shadow-indigo-900/20 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed mt-2" data-id="element-6908">
              {isLoading ? <>
                  <Loader2 size={16} className="animate-spin" data-id="element-6909" />
                  Iniciando sesión...
                </> : 'Iniciar Sesión'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center space-y-2" data-id="element-6910">
          <div className="flex items-center justify-center gap-3" data-id="element-6911">
            <a href="/terms" className="text-[11px] text-slate-500 hover:text-indigo-400 transition-colors" data-id="element-6912">
              Términos y Condiciones
            </a>
            <span className="text-slate-700" data-id="element-6913">·</span>
            <a href="/privacy" className="text-[11px] text-slate-500 hover:text-indigo-400 transition-colors" data-id="element-6914">
              Política de Privacidad
            </a>
          </div>
          <p className="text-[11px] text-slate-600 font-mono" data-id="element-6915">
            Workium AI · v1.0.0
          </p>
        </div>
      </div>
    </div>;
}